import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { UserPlus, GraduationCap, ShieldCheck, Info, Lock } from 'lucide-react';
import useNoIndex from '../hooks/useNoIndex';

export default function Register() {
  useNoIndex();
  const { register, dark } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: '', password: '', name: '' });
  const [isTeacher, setIsTeacher] = useState(false);
  const [teacherCode, setTeacherCode] = useState('');
  const [loading, setLoading] = useState(false);

  // Bandeau contextuel + redirection post-inscription si arrivée depuis route protégée
  const fromPath = location.state?.from || null;
  const fromLabel = fromPath === '/enseignant' || fromPath?.startsWith('/enseignant')
    ? "Espace Enseignant"
    : fromPath === '/preparation-ccs'
      ? "Préparation CCS 2 Roues"
      : null;

  const onChange = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (isTeacher && !teacherCode.trim()) {
      toast.error('Veuillez saisir votre code enseignant');
      return;
    }
    setLoading(true);
    try {
      const data = await register(form.email, form.password, form.name, isTeacher ? teacherCode.trim() : null);
      toast.success(data.role === 'enseignant' ? 'Compte enseignant activé !' : 'Compte créé avec succès');
      // Redirection contextuelle : page d'origine si elle existe, sinon home
      navigate(fromPath || '/');
    } catch (err) {
      toast.error(err.message || "Erreur d'inscription");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center py-8" data-testid="register-page">
      <div className="mc-card p-6 sm:p-8 w-full max-w-md">
        <div className="flex items-center gap-3 mb-5">
          <img src="/logo-c2r.png" alt="CODE 2 ROUES" className={`w-11 h-11 rounded-2xl object-cover bg-[#0b1b3a]`} />
          <div>
            <h1 className="text-xl font-black">Créer mon compte gratuit</h1>
            <p className="text-xs text-muted-foreground">Plateforme d'entraînement &amp; préparation au Code Moto 2026</p>
          </div>
        </div>

        {/* Bandeau contextuel : pourquoi l'utilisateur est ici */}
        {fromLabel && (
          <div
            className="mb-4 rounded-xl border-2 border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary-soft))]/40 p-3.5"
            data-testid="register-protected-notice"
          >
            <div className="flex items-start gap-2.5">
              <Lock className="w-4 h-4 mt-0.5 text-[hsl(var(--primary))] flex-shrink-0" />
              <div className="text-[13px] leading-snug text-foreground/85">
                <strong className="block text-foreground mb-0.5">
                  {fromLabel} — accès réservé
                </strong>
                <span className="text-foreground/70">
                  Créez votre compte gratuit ci-dessous pour débloquer cette section.
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Bannière de positionnement non lucratif */}
        <div
          className="mb-5 rounded-xl border border-[#16a34a]/25 bg-[#16a34a]/5 px-3 py-2.5 flex items-start gap-2"
          data-testid="register-positioning-banner"
        >
          <Info className="w-4 h-4 text-[#16a34a] flex-shrink-0 mt-0.5" />
          <div className="text-[12px] leading-relaxed text-foreground/85">
            <strong className="text-[#16a34a]">100 % gratuit, sans frais cachés.</strong> Code 2 Roues
            est une plateforme <strong>d'entraînement</strong> à but non lucratif. Nous
            n'organisons pas le passage de l'examen officiel — celui-ci s'effectue auprès des
            opérateurs agréés par l'État (La Poste, SGS, Dekra, Pearson VUE, Bureau Veritas, Code'nGo!).
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Nom complet</Label>
            <Input id="name" required value={form.name} onChange={onChange('name')} placeholder="Jean Dupont" data-testid="register-name-input" />
          </div>
          <div>
            <Label htmlFor="email">Adresse email</Label>
            <Input id="email" type="email" required value={form.email} onChange={onChange('email')} placeholder="vous@example.com" data-testid="register-email-input" />
          </div>
          <div>
            <Label htmlFor="password">Mot de passe</Label>
            <PasswordInput id="password" required minLength={6} value={form.password} onChange={onChange('password')} placeholder="Minimum 6 caractères" data-testid="register-password-input" />
          </div>

          <div className="mc-soft p-3 flex items-center justify-between" data-testid="register-teacher-toggle-row">
            <div className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-[hsl(var(--primary))]" />
              <div>
                <div className="text-sm font-semibold">Je suis enseignant / moniteur</div>
                <div className="text-xs text-muted-foreground">Accès aux outils pédagogiques. Code requis.</div>
              </div>
            </div>
            <Switch checked={isTeacher} onCheckedChange={setIsTeacher} data-testid="register-teacher-switch" />
          </div>

          {isTeacher && (
            <div>
              <Label htmlFor="teacherCode" className="flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-[hsl(var(--primary))]" /> Code enseignant
              </Label>
              <Input id="teacherCode" value={teacherCode} onChange={e => setTeacherCode(e.target.value.toUpperCase())} placeholder="EDU-XXXXXXXXXX" data-testid="register-teacher-code-input" style={{ fontFamily: 'monospace' }} />
              <p className="text-xs text-muted-foreground mt-1">Ce code vous est fourni par l'administration Code 2 roues.</p>
              <div className="mt-2 rounded-xl bg-[hsl(var(--primary-soft))] border border-[hsl(var(--primary))]/20 p-2.5 text-xs">
                <p className="text-foreground/80">
                  Pas de code ?{' '}
                  <Link
                    to="/rejoindre-enseignant"
                    className="font-bold text-[hsl(var(--primary))] hover:underline"
                    data-testid="register-no-code-link"
                  >
                    Faire une demande d'accès enseignant →
                  </Link>
                </p>
                <p className="text-muted-foreground mt-1">Réponse sous 48 h après examen de votre dossier.</p>
              </div>
            </div>
          )}

          <Button type="submit" disabled={loading} className="mc-btn-primary w-full h-11" data-testid="register-submit-btn">
            <UserPlus className="w-4 h-4 mr-2" /> {loading ? 'Création...' : 'Créer mon compte gratuit'}
          </Button>

          <p className="text-[11px] text-muted-foreground leading-relaxed text-center" data-testid="register-legal-mention">
            En créant un compte, vous acceptez nos{' '}
            <Link to="/mentions-legales" className="underline hover:text-[hsl(var(--primary))]">mentions légales</Link>
            {' '}et notre{' '}
            <Link to="/confidentialite" className="underline hover:text-[hsl(var(--primary))]">politique de confidentialité</Link>.
          </p>
        </form>

        <div className="mt-4 text-sm text-muted-foreground text-center">
          Déjà inscrit ?{' '}
          <Link
            to="/login"
            state={fromPath ? { from: fromPath } : undefined}
            className="font-semibold text-[hsl(var(--primary))] hover:underline"
            data-testid="register-login-link"
          >
            Se connecter
          </Link>
        </div>
      </div>
    </div>
  );
}
